import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../components/user/BCPAuthProvider";
import { useParams } from "react-router-dom";
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  Tooltip,
  ButtonBase,
  useTheme,
  Divider
} from "@mui/material";
import {
  delay,
  findGetParameter,
  getData
} from "../../components/utilities/UtilityFunctions";
import { postNotification } from "../../components/utilities/Notifications";
import ModalLoadingMessage from "../../components/modals/ModalLoadingMessage";
import moment from "moment";
import "moment-timezone";

import { ContactTOModal } from "../events/components/EventAbout/EventAboutDefault";
import { userDisplayName } from "../events/components/EventFunctions";
import {
  BCPCalendarDay,
  BCPShop,
  BCPUser
} from "../../components/icons/bcp/BCPIcons";
import Login from "../user/Login";
import {
  getEnvironmentVariable,
  isDev
} from "../../global/environmentVariables";
import { hasCookiePermission } from "../../components/layout/cookies/CookieFunctions";
import { datadogRum } from "@datadog/browser-rum";
import { useTranslation } from "react-i18next";

const IS_DEV = isDev();

export default function PurchaseConfirmation() {
  const { t: tGeneral } = useTranslation();

  let { transactionId } = useParams();
  const contactTOParam = findGetParameter("contactTO");

  const theme = useTheme();
  const [message, setMessage] = useState(null);
  const authContext = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [transaction, setTransaction] = useState(null);
  const [event, setEvent] = useState(null);
  const [player, setPlayer] = useState(null);
  const [, setTeamPlayer] = useState(null);
  const [contactTOModal, setContactTOModal] = useState(
    contactTOParam === "true" ? true : false
  );
  const [contactTOModalRefund, setContactTOModalRefund] = useState(false);
  const [showLogin, setShowLogin] = useState(false);

  const orderId = findGetParameter("orderId");

  const fetchTransaction = async (apiCounter) => {
    let queryStringParameters = {
      "expand[]": ["refund"]
    };
    if (orderId) {
      queryStringParameters.orderId = orderId;
    }

    try {
      var apiTransaction = await getData(`/transactions/${transactionId}`, {
        queryStringParameters
      });

      if (apiTransaction.status === "Pending Charge") {
        if (apiCounter > 30 && !IS_DEV) {
          if (hasCookiePermission("dataDog") && datadogRum?.addError) {
            datadogRum.addError(
              `Potential error with stripe webhook: called transactions api ${apiCounter} times`
            );
          }

          return apiTransaction;
        }
        setMessage("Checking transaction status");
        await delay(1000);
        apiTransaction = await fetchTransaction(apiCounter + 1);
        // TODO : after 30 seconds, do something
      }

      return apiTransaction;
    } catch (err) {
      return null;
    }
  };

  useEffect(() => {
    const check = async () => {
      try {
        if (transactionId) {
          setMessage("Fetching transaction information");
          var apiTransaction = await fetchTransaction(0);

          if (apiTransaction?.eventId) {
            setMessage("Fetching event information");
            var apiEvent = await getData(`/events/${apiTransaction.eventId}`);

            if (apiEvent.photoFileId) {
              apiEvent.photo = await getData(`/files/${apiEvent.photoFileId}`, {
                queryStringParameters: {}
              });
            }
            setEvent(apiEvent);
          }

          if (
            apiTransaction?.status === "Completed Charge" ||
            apiTransaction?.status === "No Charge"
          ) {
            if (apiTransaction?.playerId) {
              setMessage("Fetching player information");
              let apiPlayer = await getData(
                `/players/${apiTransaction.playerId}`
              );

              setPlayer(apiPlayer);
            }
            if (apiTransaction?.teamPlayerId) {
              setMessage("Fetching player information");
              let apiPlayer = await getData(
                `/teamplayers/${apiTransaction.teamPlayerId}`
              );

              setTeamPlayer(apiPlayer);
            }
          }

          setTransaction(apiTransaction);
          setLoading(false);
        }
      } catch (err) {
        postNotification("error", err);
        setLoading(false);
      }
    };
    check();
    // eslint-disable-next-line
  }, []);
  if (showLogin) {
    return <Login />;
  }

  return (
    <Box sx={{ width: "100%" }}>
      {!loading && (
        <Container
          sx={{
            pt: 2,
            pb: 2
          }}
          maxWidth="lg"
        >
          <Box
            sx={{
              height: ["400px", "300px"],
              maxHeight: ["400px", "300px"],
              minHeight: ["400px", "300px"],
              overflow: "hidden",
              position: "absolute",
              top: "61px",
              left: 0,
              width: "100%",
              zIndex: "-1"
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: "0",
                left: "0",
                width: "100%"
              }}
            >
              <picture
                content={
                  event?.photo?.url ||
                  event?.photoUrl ||
                  `${getEnvironmentVariable(
                    "SYSTEM_IMAGES_CLOUDFRONT"
                  )}/images/EventImage${
                    theme?.palette?.mode === "dark" ? "Dark" : ""
                  }.png`
                }
              >
                <source
                  srcset={
                    event?.photo?.url ||
                    event?.photoUrl ||
                    `${getEnvironmentVariable(
                      "SYSTEM_IMAGES_CLOUDFRONT"
                    )}/images/EventImage${
                      theme?.palette?.mode === "dark" ? "Dark" : ""
                    }.png`
                  }
                  sizes="100vw"
                />
                <img
                  data-automation="listing-hero-image"
                  alt=""
                  style={{
                    transform: "translate(-50%, -50%)",
                    filter: "blur(50px) brightness(.9)",
                    display: "inline-block",
                    width: "200%",
                    position: "relative",
                    top: "50%",
                    left: "50%"
                  }}
                />
              </picture>
            </Box>
          </Box>
          <Grid container sx={{ gap: 2, mb: 2 }}>
            <Grid item xs={12}>
              <Grid container>
                <Grid item xs={12} sm={7}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      maxWidth: "100%",
                      minWidth: "100%",
                      paddingTop: "0px !important",
                      backgroundColor: "rgb(46 55 77 / 44%);",
                      "&:hover": {
                        opacity: [0.9, 0.8, 0.7]
                      }
                    }}
                  >
                    <img
                      src={
                        event?.photo?.url ||
                        event?.photoUrl ||
                        `${getEnvironmentVariable(
                          "SYSTEM_IMAGES_CLOUDFRONT"
                        )}/images/EventImage${
                          theme?.palette?.mode === "dark" ? "Dark" : ""
                        }.png`
                      }
                      alt={event?.name}
                      style={{ maxWidth: "100%", maxHeight: 300 }}
                    />
                  </Box>
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={5}
                  sx={{
                    ml: [2],
                    mr: [-2],
                    display: "flex",
                    alignItems: "center",
                    maxWidth: [
                      "calc(100% - 36px) !important",
                      "calc(41.666667% - 36px) !important"
                    ],
                    minWidth: [
                      "calc(100% - 36px) !important",
                      "calc(41.666667% - 36px) !important"
                    ],
                    "& .MuiTypography-root": {
                      overflowWrap: "break-word",
                      wordWrap: "break-word"
                    }
                  }}
                >
                  {authContext?.authState?.identity?.userId && (
                    <Grid
                      container
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        backgroundColor: `${theme?.palette?.background.default}80`
                      }}
                    >
                      <Grid item xs={12}>
                        <Typography variant="h4" fontWeight={700} gutterBottom>
                          <Link href={`/event/${event?.id}`} underline="none">
                            {event?.name}
                          </Link>
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography gutterBottom>
                          <Box
                            sx={{
                              minWidth: "35px",
                              verticalAlign: "bottom",
                              display: "inline-flex"
                            }}
                          >
                            <Tooltip title="Event Date">
                              <BCPCalendarDay
                                sx={{
                                  color: theme?.palette?.text?.secondary,
                                  verticalAlign: "bottom"
                                }}
                              />
                            </Tooltip>
                            <Typography sx={{ ml: 2 }}>
                              {(event?.timeZone
                                ? moment(event?.eventDate).tz(event?.timeZone)
                                : moment(event?.eventDate)
                              ).format("LL")}
                            </Typography>
                          </Box>
                        </Typography>
                      </Grid>
                      {Boolean(event?.gameStoreName) && (
                        <Grid item xs={12}>
                          <Typography gutterBottom>
                            <Box
                              sx={{
                                minWidth: "35px",
                                verticalAlign: "bottom",
                                display: "inline-flex"
                              }}
                            >
                              <Tooltip title="Host Organisation">
                                <BCPShop
                                  sx={{
                                    color: theme?.palette?.text?.secondary,
                                    verticalAlign: "bottom"
                                  }}
                                />
                              </Tooltip>
                            </Box>
                            <Link
                              underline="none"
                              color="link.main"
                              href={`/organization/${
                                event?.gameStoreId || event?.gameStore?.id
                              }`}
                            >
                              {`${event.gameStoreName}`.trim()}
                            </Link>
                          </Typography>
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        <Typography gutterBottom>
                          <Box
                            sx={{
                              minWidth: "35px",
                              verticalAlign: "bottom",
                              display: "inline-flex"
                            }}
                          >
                            <Tooltip title="Event Organizer">
                              <BCPUser
                                sx={{
                                  color: theme?.palette?.text?.secondary,
                                  verticalAlign: "bottom"
                                }}
                              />
                            </Tooltip>
                          </Box>
                          <Link
                            underline="none"
                            href="#"
                            onClick={(e) => {
                              setContactTOModal(true);
                            }}
                          >
                            {event?.ownerFirstName || event?.ownerLastName
                              ? userDisplayName(
                                  event?.ownerFirstName,
                                  event?.ownerLastName
                                )
                              : "Contact Organizer"}
                          </Link>
                          <ContactTOModal
                            open={contactTOModal}
                            setOpen={setContactTOModal}
                            authContext={authContext}
                            event={event}
                            tGeneral={tGeneral}
                          />
                        </Typography>
                      </Grid>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Grid>

            <Grid
              container
              xs={12}
              sx={{
                pl: 2,
                pr: 2,
                mt: 2,
                mb: 2
              }}
            >
              <Grid item xs={12}>
                <Typography variant="h3" fontWeight="700" gutterBottom>
                  {transaction?.amount > 0 ? "Purchase" : "Registration"}{" "}
                  Confirmation{" "}
                  {transaction?.status ? `- ${transaction?.status}` : ""}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body1" paragraph>
                  Thank you for choosing{" "}
                  {getEnvironmentVariable("COMPANY_NAME")}.
                </Typography>
                <Divider sx={{ mt: 2, mb: 2 }} />
              </Grid>

              {transaction?.orderId && (
                <Grid item xs={12}>
                  <Typography variant="body1" paragraph>
                    Your purchase confirmation number is{" "}
                    <span style={{ fontWeight: "bold" }}>
                      {transaction?.orderId?.toUpperCase()}
                    </span>
                    .
                  </Typography>
                </Grid>
              )}

              {transaction?.amount > 0 && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h4" fontWeight="700" gutterBottom>
                      Transaction Information
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="body1">
                      Paid:{" "}
                      <span
                        style={{
                          fontWeight: "bold"
                        }}
                      >
                        {" "}
                        {`${transaction?.currency?.toUpperCase()} ${(
                          transaction?.amount / 100
                        ).toFixed(2)}`}{" "}
                        (
                        {(event?.timeZone
                          ? moment(transaction?.created_at).tz(event?.timeZone)
                          : moment(transaction?.created_at)
                        ).format("LL LT")}
                        )
                      </span>
                    </Typography>
                  </Grid>

                  {transaction?.refund?.amount && (
                    <Grid item xs={12} sx={{ minWidth: "100px" }}>
                      <Typography variant="body1">
                        Refunded:{" "}
                        <span
                          style={{
                            fontWeight: "bold"
                          }}
                        >
                          {`${transaction?.currency?.toUpperCase()} ${(
                            transaction?.refund?.amount / 100
                          ).toFixed(2)}`}{" "}
                          (
                          {(event?.timeZone
                            ? moment(transaction?.refund?.created_at).tz(
                                event?.timeZone
                              )
                            : moment(transaction?.refund?.created_at)
                          ).format("LL LT")}
                          )
                        </span>
                      </Typography>
                    </Grid>
                  )}
                </>
              )}
              {event?.refundDate &&
                transaction?.status === "Completed Charge" && (
                  <>
                    {moment(event?.refundDate).isAfter(moment()) ? (
                      <Typography variant="body1" paragraph>
                        <ButtonBase
                          color="warning"
                          variant="contained"
                          onClick={(e) => {
                            setContactTOModalRefund(true);
                          }}
                          sx={{
                            margin: "inherit",
                            mb: "-4px",
                            mt: "-4px",
                            fontFamily: "inherit",
                            fontWeight: "inherit",
                            fontSize: "inherit",
                            lineHeight: "inherit",
                            letterSpacing: "inherit",
                            color: "link.main"
                          }}
                        >
                          Request Refund
                        </ButtonBase>{" "}
                        until:{" "}
                        <span
                          style={{
                            fontWeight: "bold"
                          }}
                        >
                          {(event?.timeZone
                            ? moment(event?.refundDate).tz(event?.timeZone)
                            : moment(event?.refundDate)
                          ).format("LL LT")}
                        </span>
                      </Typography>
                    ) : (
                      <Typography variant="body1" paragraph>
                        Refunds are no longer available.
                      </Typography>
                    )}
                    <ContactTOModal
                      open={contactTOModalRefund}
                      setOpen={setContactTOModalRefund}
                      authContext={authContext}
                      event={event}
                      subject={`${event?.name} - Refund Request`}
                      tGeneral={tGeneral}
                    />
                  </>
                )}

              {!authContext?.authState?.identity?.userId && (
                <Grid item xs={12}>
                  <Typography variant="body1" paragraph>
                    Please{" "}
                    <Link
                      sx={{ textDecoration: "none", cursor: "pointer" }}
                      onClick={() => setShowLogin(true)}
                    >
                      login{" "}
                    </Link>
                    to view transaction details
                  </Typography>
                </Grid>
              )}

              {authContext?.authState?.identity?.userId && (
                <>
                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Typography variant="h4" fontWeight="700" gutterBottom>
                      {event?.teamEvent || event?.doublesEvent
                        ? "Team"
                        : "Player"}{" "}
                      Information
                    </Typography>
                  </Grid>

                  {(player?.userId &&
                    authContext?.authState?.identity?.userId ===
                      player.userId) ||
                  (transaction?.userId &&
                    authContext?.authState?.identity?.userId ===
                      transaction.userId) ? (
                    <Grid container spacing={1} sx={{ mb: 1 }}>
                      {(event?.teamEvent || event?.doublesEvent) && (
                        <Grid item xs={12}>
                          Team Name:{" "}
                          <span style={{ fontWeight: "bold" }}>
                            {transaction?.teamName}
                          </span>
                        </Grid>
                      )}
                      <Grid item xs={12}>
                        {event?.teamEvent ||
                        event?.doublesEvent ||
                        event?.format?.teamEvent ||
                        event?.format?.doublesEvent
                          ? "Captain"
                          : "Player"}{" "}
                        Email:{" "}
                        <span style={{ fontWeight: "bold" }}>
                          {transaction?.playerEmail}
                        </span>
                      </Grid>
                      <Grid item xs={12}>
                        {event?.teamEvent ||
                        event?.doublesEvent ||
                        event?.format?.teamEvent ||
                        event?.format?.doublesEvent
                          ? "Captain"
                          : "Player"}{" "}
                        Name:{" "}
                        <span style={{ fontWeight: "bold" }}>
                          {transaction?.playerName}
                        </span>
                      </Grid>
                    </Grid>
                  ) : (
                    <Grid item xs={12}>
                      <Divider sx={{ mt: 2, mb: 2 }} />
                      <Typography variant="body1" paragraph>
                        {`To view the player details, please login as either the Purchaser or Player assigned to the ticket.`}
                      </Typography>
                    </Grid>
                  )}

                  {player?.userId &&
                    authContext?.authState?.identity?.userId ===
                      player.userId && (
                      <Grid item xs={12}>
                        <Divider sx={{ mt: 2, mb: 2 }} />

                        <Typography variant="body1" paragraph>
                          To submit your{" "}
                          {event?.gameSystem?.listLabel || "list"}, select a
                          team or check in, click{" "}
                          <Link
                            href={`/event/${event?.id}?active_tab=player-details`}
                            underline="none"
                          >
                            here
                          </Link>
                          .
                        </Typography>
                      </Grid>
                    )}
                </>
              )}
            </Grid>
          </Grid>
        </Container>
      )}
      {loading && (
        <ModalLoadingMessage
          open
          header="Purchase Confirmation"
          message={message}
        />
      )}
    </Box>
  );
}
